import { reactive, inject, ref, computed, provide } from 'vue';

import AuthLoginComponent from '@/components/auth/blocks/login/login.vue';
import EventCardComponent from '@/components/events/blocks/event-card/event-card.vue';

/**
 * Events Purchase View
 * @module Events Purchase View
 *
 * @prop { Config } config Component configuration
 * @prop { User } [user] Authenticated user details
 * @prop { eventType } [String] Type of event to display
 *
 */

export default {
  name: 'EventsPurchaseView',
  props: {
    config: {
      type: Object,
      required: false,
      default: {},
    },
    user: {
      type: Object,
      required: false,
      default: null
    },
    eventType: {
      type: String,
      required: false,
      default: null,
    },
    sortBy: {
      type: String,
      required: false,
      default: 'created_at'
    },
    sortOrder: {
      type: String,
      required: false,
      default: 'desc'
    },
    perPage: {
      type: Number,
      required: false,
      default: 20
    },
    suppress: {
      type: Array,
      required: false,
      default: []
    },
    darkTheme: {
      type: Boolean,
      required: false,
      default: false,
    },
    replaysOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    AuthLoginComponent,
    EventCardComponent,
  },
  setup(props) {
    let config = inject('config');
    config = { ...config, ...props.config };
    provide('config', config);

    const authProvider = inject("authProvider");
    const eventProvider = inject('eventProvider');

    const state = reactive({
      showNotification: ref(false),
      notification: ref(null),
      user: computed(() => authProvider().getUser),
      hasUser: computed(() => authProvider().hasUser),
      isLoadingUser: computed(() => authProvider().isLoading),
      hasCookie: computed(() => authProvider().hasCookie),
      showLogin: ref(false),
      userEvents: computed(() => eventProvider().getUserEvents),
      events: ref(null),
      pastEvents: computed( () => {
        if(state.events.length) {
          return state.events.filter((event) => {
            let now = new Date();
            let eventDate = new Date(event.dates.ends);
            return (now > eventDate && !state.suppress.includes(event.id));
          });
        }
        return [];
      }),
      futureEvents: computed( () => {
          if(state.events.length) {
            return state.events.filter((event) => {
              let now = new Date();
              let eventDate = new Date(event.dates.ends);
              return (now <= eventDate && !state.suppress.includes(event.id));
            });
          }
          return [];
      }),
      active: ref('past'),
      suppress: ref(props.suppress),
    });

    const inThePast = (datetime) => {
      let date = new Date (datetime);
      let now = new Date();

      return (now > date);
    }

    const setDefaultTab = () => {
      if(props.replaysOnly) {
        state.active = 'past';
        return;
      }
      if (state.pastEvents.length) {
        let replaySale = state.pastEvents.some(event => {
          if (event.sale.price) {
            if (event.sale.dates.ends_at) {
              return !inThePast(event.sale.dates.ends_at);
            } else {
              return true;
            }
          }
        });
        if (replaySale) {
          state.active = 'past';
          return;
        }
        if (state.futureEvents.length) {
          state.active = 'future';
        }
      }
    };

    const handleResponse = async (response) => {
      if (response.status === 'error' || response.success === false) {
        state.showNotification = true;
        state.notification = response.data.message || response.data.data.message || response.data.data;
      } else {
        state.showNotification = false;
        state.notification = null;
      }
    };

    const handleLoginResponse = async (response) => {
      if (response.status === 'error' || response.success === false) {
        state.showLogin = true;
        state.notification = response.data.message || response.data.data.message || response.data.data;
      } else {
        state.showLogin = false;
        state.notification = null;
        document.body.classList.remove('koc-has-overlay');
      }
    };

    const loadUserEvents = async (user, token) => {
      state.showNotification = true;
      state.notification = 'Checking User Events...';
      let response = await eventProvider().loadUserEvents(user, token, config);
      if (response.status === 'error' || response.success === false) {
        state.showNotification = true;
        state.notification = response.data.message || response.data.data.message || response.data.data;
      } else {
        state.showNotification = false;
        state.showLogin = false;
      }
      return;
    }

    const loadConferenceEvents = async () => {
      state.showNotification = true;
      state.notification = 'Loading Events...';
      let response = await eventProvider().loadEvents(config, props.eventType, props.perPage, props.sortBy, props.sortOrder);
      if (response.status === 'error' || response.success === false) {
        state.showNotification = true;
        state.notification = response.data.message || response.data.data.message || response.data.data;
      } else {
        state.events = response.data.data;
        state.showNotification = false;
        state.notification = null;
      }

      return;
    }

    const loadUser = async (user) => {
      state.showNotification = true;
      state.notification = 'Loading User...';

      const { id, token } = user;

      let response = await authProvider().loadUser(id, token, config);
      await handleResponse(response);

      if ('success' === response.status) {
        const { data } = response.data;
        response = await loadUserEvents(data.id, data.token)
      }
    };

    const login = async (data) => {
      state.notification = 'Validating Credentials...';

      const { username, password } = data;
      const response = await authProvider().login(username, password, config);
      await handleLoginResponse(response);

      if ('success' === response.status) {
        const { data } = response.data;
        await loadUser(data);
        state.showLogin = false;
      }
    };

    const clearNotification = async => state.showNotification = false;

    const initialize = async () => {
      if (props.user && !state.hasUser) {
        await loadUser(props.user);
      }
      else {
        if (state.hasCookie) return;
        const cookie = authProvider().loadUserCookie(config);
        if (cookie) await loadUser(cookie)
      }

      await loadConferenceEvents();
      setDefaultTab();

    }

    initialize();

    document.addEventListener('koc-login', async (event) => {
      if (state.hasUser) {
        await eventProvider().removeUserEvents(config);
        await authProvider().logout(config);
      }
      return await loadUser(event.detail.user);
    })

    document.addEventListener('koc-logout', async () => {
      if (state.hasUser) {
        await eventProvider().removeUserEvents(config);
        return await authProvider().logout(config);
      }
    })


    return { login, clearNotification, loadUser, loadConferenceEvents, state };
  },
  methods: {
    showLogin: async function () {
      this.state.showLogin = true;
      document.body.classList.add('koc-has-overlay');
    },
    closeLogin: async function () {
      this.state.showLogin = false;
      document.body.classList.remove('koc-has-overlay');
    },
  },
};
